"use client"

// Adapted from shadcn/ui's MIT-licensed Radix composition.
// Source and complete notices: docs/dropdown-third-party-notices.md.
import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { ChevronDown, ChevronUp, type LucideIcon } from "lucide-react"
import { dropdownIconFor, dropdownIconPaths, type DropdownIconName } from './dropdown-icons'
import { Checkmark as Check } from "./checkmark"

import { cn } from "@/lib/utils"
import { useDropdownTheme, useDropdownTopLayer } from './dropdown-theme'

const SelectAnchor = React.createContext<{ ref: React.RefObject<HTMLButtonElement | null>; container: HTMLElement | null; register: (element: HTMLButtonElement | null) => void } | null>(null)

function Select(props: React.ComponentProps<typeof SelectPrimitive.Root>) {
  const anchor = React.useRef<HTMLButtonElement>(null)
  const [container, setContainer] = React.useState<HTMLElement | null>(null)
  const register = React.useCallback((element: HTMLButtonElement | null) => { anchor.current = element; setContainer(element?.closest<HTMLElement>('dialog,[role="dialog"]') ?? null) }, [])
  return <SelectAnchor.Provider value={{ ref: anchor, container, register }}><SelectPrimitive.Root {...props} /></SelectAnchor.Provider>
}

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

function ContextIcon({ name }: { name: DropdownIconName }) {
  return <svg data-slot="select-context-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: dropdownIconPaths[name] }} />
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { icon?: LucideIcon | false }
>(({ className, children, icon: Icon, ...props }, ref) => {
  const register = React.useContext(SelectAnchor)?.register
  const composedRef = React.useCallback((element: HTMLButtonElement | null) => { register?.(element); if (typeof ref === 'function') return ref(element); if (ref) ref.current = element }, [register, ref])
  return (
  <SelectPrimitive.Trigger
    ref={composedRef}
    data-slot="select-trigger"
    className={cn(
      "form-control relative flex h-11 w-full min-w-0 items-center gap-2 py-2.5 ps-3.5 pe-10 text-start text-base leading-5 md:text-sm",
      className
    )}
    {...props}
  >
    <span data-slot="select-value-container" className="min-w-0 flex-1 truncate">{Icon === false ? null : Icon ? <Icon data-slot="select-context-icon" aria-hidden="true" /> : <ContextIcon name={dropdownIconFor(`${props['aria-label'] ?? ''} ${props.id ?? ''}`)} />}{children}</span>
    <SelectPrimitive.Icon asChild>
      <ChevronDown data-slot="select-chevron" aria-hidden="true" className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 text-muted-foreground" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", sideOffset = 6, collisionPadding = 12, ...props }, ref) => {
  const context = React.useContext(SelectAnchor), anchor = context?.ref
  const fallback = React.useRef<HTMLButtonElement>(null)
  const [popup, setPopup] = React.useState<HTMLDivElement | null>(null)
  const composedRef = React.useCallback((element: HTMLDivElement | null) => { setPopup(element); if (typeof ref === 'function') return ref(element); if (ref) ref.current = element }, [ref])
  useDropdownTheme(anchor ?? fallback, popup)
  // A native modal's top layer is above document.body. Stay in its focus scope.
  const container = context?.container
  useDropdownTopLayer(popup, container)
  return (
  <SelectPrimitive.Portal container={container}>
    <SelectPrimitive.Content
      ref={composedRef}
      data-slot="select-content"
      className={cn(
        "dropdown-content relative z-50 flex max-h-[min(360px,var(--radix-select-content-available-height,360px))] min-w-[8rem] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded border bg-popover text-popover-foreground shadow-lg outline-none",
        position === "popper" &&
          "w-max min-w-[var(--radix-select-trigger-width)]",
        className
      )}
      position={position}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "min-h-0 w-full min-w-0 flex-1 p-1"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-3 py-2 text-xs font-medium tracking-wide text-muted-foreground", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { icon?: LucideIcon }
>(({ className, children, icon: Icon, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-slot="select-item"
    className={cn(
      "dropdown-option relative flex min-h-9 w-full cursor-default select-none items-center rounded-sm py-2 ps-3 pe-9 text-sm leading-5 outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    title={typeof children === 'string' ? children : undefined}
    {...props}
  >
    <span data-slot="select-item-indicator" className="absolute end-2.5 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-3.5 w-3.5" />
      </SelectPrimitive.ItemIndicator>
    </span>

    {Icon && <Icon data-slot="select-item-icon" aria-hidden="true" />}
    <SelectPrimitive.ItemText data-slot="select-item-text" className="min-w-0 flex-1 truncate">{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
